<template>
    <nav
        :aria-label="$t('topNav.Top')"
        class="pendo-top-nav"
        :class="{
            impersonating: isImpersonating,
            'agent-mode': isAgentModeActive,
            'sales-trial-expired': isOnTrialExpiredRoute,
            'is-blurred': isBlurred,
        }"
        data-cy="top-nav">
        <div class="pendo-top-nav__nav-content">
            <expand-collapse-nav-button
                v-if="!isAgentModeActive"
                is-top-nav />
            <pendo-button
                v-if="isImpersonating"
                v-pendo-tooltip="{
                    content: $t('topNav.Unimpersonate'),
                    arrow: true,
                }"
                class="top-nav__trigger top-nav__button-trigger"
                type="tertiary"
                size="small"
                :aria-label="$t('topNav.Unimpersonate')"
                @click="unimpersonate"
                @mouseenter="handleMouseEnterUnimpersonate"
                @mouseleave="handleMouseLeaveUnimpersonate">
                <pendo-icon
                    type="user-x"
                    size="20"
                    stroke-width="2"
                    :fill="hoveringUnimpersonate ? 'var(--icon-stroke-base)' : 'var(--icon-stroke-inverse)'" />
            </pendo-button>

            <div
                v-if="!isAgentModeActive"
                class="pendo-top-nav__left-options-container">
                <pendo-button
                    v-pendo-tooltip="{
                        content: $t('topNav.Back to previous page'),
                        arrow: true,
                    }"
                    class="pendo-top-nav__back-button"
                    pendo-analytics="back-button-nav-item"
                    type="tertiary"
                    size="small"
                    prefix-icon="arrow-left"
                    icon-size="16"
                    :disabled="endOfHistory || isOnTrialExpiredRoute"
                    :aria-label="$t('topNav.Back to previous page')"
                    @click="handleBack" />
                <!-- Teleport target for top-nav-left-options-wrapper. -->
                <div class="pendo-top-nav__left-options" />
            </div>
            <template v-if="isAgentModeActive">
                <img
                    v-if="!isOnAgentModeConversationPage"
                    src="@img/pendo-chevron.svg"
                    alt="Pendo chevron"
                    width="21"
                    height="21"
                    class="header-logo">
                <pendo-button
                    v-else
                    v-pendo-tooltip="{
                        content: agentModeConversationBackLabel,
                        arrow: true,
                    }"
                    class="pendo-top-nav__back-button"
                    pendo-analytics="back-button-nav-item"
                    type="tertiary"
                    size="small"
                    prefix-icon="arrow-left"
                    icon-size="16"
                    :disabled="isOnTrialExpiredRoute"
                    :aria-label="agentModeConversationBackLabel"
                    @click="handleAgentModeConversationBack" />
            </template>
            <div class="pendo-top-nav__right-options">
                <ask-pendo-context-button
                    v-if="hasAgentMode && !isOnLeoPage"
                    ref="askLeoButton"
                    context-follows-page
                    :context="askLeoContext"
                    :suggested-questions="askLeoSuggestedQuestions"
                    :active="isLeoDrawerOpen"
                    :disabled="isLeoPageContextLoading"
                    :working="isLeoWorkingOutOfSight"
                    size="small"
                    class="pendo-top-nav__ask-leo-button" />

                <div
                    v-if="isAdoptEndUser && trainingAttributes?.productName"
                    class="pendo-top-nav__oem-product-name">
                    <oem-product-name />
                    <pendo-divider
                        direction="vertical"
                        decorative
                        stroke="var(--divider-base)"
                        width="8px"
                        height="20px" />
                </div>
                <top-nav-right-options-wrapper :is-impersonating="isImpersonating" />
            </div>
        </div>
        <div
            v-if="isImpersonating"
            class="pendo-top-nav__candy-cane-bar" />
    </nav>
</template>

<script>
import { PendoButton, PendoIcon, PendoTooltip, PendoDivider } from '@pendo/components-vue3';
import OemProductName from '@/common/oem-product-name/oem-product-name.vue';
import ExpandCollapseNavButton from '@/common/expand-collapse-nav-button/expand-collapse-nav-button.vue';
import TopNavRightOptionsWrapper from '@/top-nav/components/top-nav-right-options-wrapper/top-nav-right-options-wrapper.vue';
import AskPendoContextButton from '@/ask-pendo/components/ask-pendo-context-button.vue';

import { mapState as mapPiniaState } from 'pinia';
import { useLeftNavStore } from '@/stores/left-nav.store';
import { useLeoPageContextStore } from '@/stores/leo-page-context.store';
import { hasAgentModeAccess } from '@/vue-router/route-guards.utils';
import { resolveStarterQuestions } from '@/ask-pendo/utils/leo-starter-questions';
import {
    isAgentModePanelOpen,
    isLeoConversationRoute,
    isLeoPageRoute,
    subscribeAgentModePanelChange, wantsNewPanelConversation,
} from '@/vue-router/agent-mode-panel.utils';
import { useAskPendoStore } from '@/stores/ask-pendo.store.js';
import { useAuthStore } from '@/stores/auth.store';
import { useSubscriptionStore } from '@/stores/subscription.store';

export default {
    name: 'TopNav',
    components: {
        PendoButton,
        PendoDivider,
        PendoIcon,
        OemProductName,
        ExpandCollapseNavButton,
        TopNavRightOptionsWrapper,
        AskPendoContextButton,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        isImpersonating: {
            type: Boolean,
            default: false,
        },
        homeSubscription: {
            type: Object,
            required: true,
        },
    },
    emits: ['toggle'],
    data () {
        return {
            hoveringUnimpersonate: false,
            endOfHistory: false,
            agentModePanelUrlRevision: 0,
        };
    },
    computed: {
        ...mapPiniaState(useLeftNavStore, ['isBlurred']),
        ...mapPiniaState(useLeoPageContextStore, {
            leoPageContext: 'context',
            leoPageSuggestedQuestions: 'suggestedQuestions',
            isLeoPageContextReady: 'isPageContextReady',
            isLeoPageContextLoading: 'isPageContextLoading',
        }),
        ...mapPiniaState(useSubscriptionStore, {
            isAdoptEndUser: 'isAdoptEndUser',
            trainingAttributes: 'trainingAttributes',
        }),
        ...mapPiniaState(useAuthStore, ['hasSegmentFlag']),
        ...mapPiniaState(useAskPendoStore, ['pendingConversationIds']),
        isAgentModeActive () {
            const route = this.$route.name;

            return this.hasAgentMode && route === 'LeoConversation';
        },
        isOnAgentModeConversationPage () {
            const route = this.$route?.name;

            return route === 'LeoConversation';
        },
        agentModeConversationBackLabel () {
            return this.endOfHistory
                ? this.$t('topNav.Back to Leo')
                : this.$t('topNav.Back to previous page');
        },
        hasAgentMode () {
            return hasAgentModeAccess();
        },
        // The homepage has its own Leo input, so this button would duplicate it — until
        // hideLeoHomepageInput takes it away. Once that input goes for good, drop the flag check
        // and LeoHomepage from isLeoPageRoute, or the button stays hidden on a page with no input.
        isOnLeoPage () {
            if (isLeoConversationRoute(this.$route)) {
                return true;
            }

            return isLeoPageRoute(this.$route) && !this.hasSegmentFlag('hideLeoHomepageInput');
        },
        isOnTrialExpiredRoute () {
            return this.$route?.name === 'TrialExpired';
        },
        // Agent Analytics toggles the panel via history.replaceState, so $route alone is not enough.
        isLeoDrawerOpen () {
            return this.agentModePanelUrlRevision >= 0 && isAgentModePanelOpen(this.$route);
        },
        // The open drawer carries the chats rail, which already shows every working chat, so the
        // nav only speaks when Leo is nowhere on screen. Its own pages are covered by the v-if.
        isLeoWorkingOutOfSight () {
            return this.hasSegmentFlag('leoWebsockets')
                && !this.isLeoDrawerOpen
                && this.pendingConversationIds.length > 0;
        },
        askLeoContext () {
            return this.isLeoPageContextReady ? this.leoPageContext : {};
        },
        askLeoSuggestedQuestions () {
            return resolveStarterQuestions({
                route: this.$route,
                $t: this.$t,
                pageSuggestedQuestions: this.leoPageSuggestedQuestions,
                isPageContextReady: this.isLeoPageContextReady,
            });
        },
        // Let a publish in progress finish, so the conversation is created with the page's context
        // rather than patched with it afterwards by the drawer.
        canOpenLeoFromQuery () {
            return this.hasAgentMode
                && !this.isOnLeoPage
                && !this.isLeoPageContextLoading
                && wantsNewPanelConversation(this.$route);
        },
    },
    watch: {
        $route: {
            handler () {
                this.checkHistoryState();
            },
            deep: true,
            immediate: true,
        },
        canOpenLeoFromQuery: {
            handler (canOpen) {
                if (!canOpen) {
                    return;
                }

                // Deferred so the button exists to be asked on the first render, and read again
                // because the page can publish its context or navigate away in the meantime.
                this.$nextTick(() => {
                    if (!this.canOpenLeoFromQuery) {
                        return;
                    }

                    this.$refs.askLeoButton?.openConversationDrawer({ replace: true });
                });
            },
            immediate: true,
        },
    },
    mounted () {
        this.unsubscribeAgentModePanelChange = subscribeAgentModePanelChange(() => {
            this.agentModePanelUrlRevision += 1;
        });
    },
    beforeUnmount () {
        this.unsubscribeAgentModePanelChange?.();
    },
    methods: {
        async unimpersonate () {
            await this.$router.pendo.switchSubscription(this.homeSubscription.id);
        },
        handleMouseEnterUnimpersonate () {
            this.hoveringUnimpersonate = true;
        },
        handleMouseLeaveUnimpersonate () {
            this.hoveringUnimpersonate = false;
        },
        handleBack () {
            if (this.endOfHistory) {
                return;
            }

            if (this.$route.name === 'AiAgentExperiment') {
                this.$router.replace(this.getAiAgentExperimentBackRoute());

                return;
            }

            this.$router.back();
        },
        getAiAgentExperimentBackRoute () {
            const query = { tab: 'experiments' };
            const { reportId, aiAgentId } = this.$route.query;

            if (reportId) {
                return { name: 'AiAgentReport', params: { reportId }, query };
            }

            if (aiAgentId) {
                query.aiAgentId = aiAgentId;
            }

            return { name: 'AiAgentReport', query };
        },
        handleAgentModeConversationBack () {
            if (!this.endOfHistory) {
                this.$router.back();

                return;
            }

            const target = { name: 'LeoHomepage' };

            this.$router.push(this.$router.pendo.toPath(target));
        },
        checkHistoryState () {
            this.endOfHistory = !this.$router?.options?.history?.state?.back;
        },
    },
};
</script>

<!-- eslint-disable internal-rules/no-hardcoded-colors -->
<style scoped lang="less">
@import '@/side-nav/nav.style.less';

.pendo-top-nav {
    position: relative;
    width: 100%;
    min-height: 50px;
    max-height: 50px;
    z-index: 499;
    display: flex;
    flex-direction: column;
    background-color: var(--surface-default-background);
    border-bottom: 1px solid var(--surface-default-border);
    overflow: hidden;

     &.sales-trial-expired {
            .agent-mode-toggle {
                cursor: not-allowed;
                opacity: 0.7;

                * {
                    pointer-events: none;
                }
            }
    }

    &__nav-content {
        display: flex;
        flex-grow: 1;
        align-items: center;
        padding: 10px 16px;
        gap: 6px;
        height: 100%;

        #expand-collapse-nav-button {
            flex-shrink: 0;
            display: none;

            :deep(svg) {
                width: 18px;
                height: 18px;
            }
        }

        .screen-size(@zero, @large, {
            padding: 10px;

            #expand-collapse-nav-button {
                display: flex;
            }
        });
    }

    &__left-options-container {
        display: flex;
        align-items: center;
        gap: 16px;
        min-width: 0;
        margin: -4px;
        padding: 4px;
        overflow: clip;
        white-space: nowrap;
    }

    &__left-options {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 10px;
        min-width: 0;

        .screen-size(@zero, @medium, {
            display: none;
        });

        :deep(.pendo-button + .pendo-button) {
            margin: 0;
        }
    }

    &__back-button {
        width: 32px;
        height: 32px;
        padding: 4px;
        border-radius: 5px;
        border: 1px solid var(--iconButton-border-base);
        flex-shrink: 0;

        &:focus-visible {
            outline: 2px solid var(--text-body-default-base);
            outline-offset: -2px;
        }

        :deep(.icon-prefix) {
            padding: 0;
        }

        :deep(.pendo-icon) {
            transition: transform 200ms linear;
        }

        &.is-disabled {
            border: 1px solid var(--iconButton-border-base);

            &:hover {
                border: 1px solid var(--iconButton-border-base);
            }
        }

        &:hover:not([aria-disabled='true']) {
            background-color: var(--surface-default-hover);

            :deep(.pendo-icon) {
                transform: translateX(-2px);
            }
        }
    }

    &__ask-leo-button {
        display: flex;
        align-items: center;
        flex-shrink: 0;

        :deep(.ask-pendo-context-button) {
            padding: 0 8px 0 2px;
        }
    }

    &__oem-product-name {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    &__right-options {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-grow: 1;
        gap: 6px;
        margin-left: 10px;

        .v-popover {
            height: 32px;

            &:last-of-type {
                margin-left: 4px;
            }
        }
    }

    &.impersonating {
        .pendo-top-nav__nav-content {
            padding: 7px 10px 6px 10px;
        }
    }

    &.agent-mode {
        border-bottom: none;
    }

    &__candy-cane-bar {
        height: 6px;
        width: 100%;
        background: linear-gradient(
            90deg,
            var(--nav-candy-cane-bar-gradient-1-base) 0%,
            var(--nav-candy-cane-bar-gradient-2-base) 34.62%,
            var(--nav-candy-cane-bar-gradient-1-base) 67.31%,
            var(--nav-candy-cane-bar-gradient-2-base) 100%
        );
    }

    .agent-mode-toggle {
        margin-right: 6px;
        display: flex;
        flex-shrink: 0;
        gap: 8px;
        align-items: center;
        justify-content: center;

        :deep(.pendo-toggle__label) {
            color: var(--text-body-default-base);
            font-weight: 600;
        }

        &.is-checked {
            :deep(.pendo-toggle__slide) {
                background-color: var(--toggle-background-ai-base);
            }
        }
    }

    .pendo-button + .pendo-button {
        margin: 0;
    }
}
</style>

<style lang="less">
.top-nav__popover {
    border-radius: 4px;
    z-index: 20001; /* needs to go above side-panels, which some are set at 20000 to go over the RC */
    overflow-y: auto;

    &-content {
        max-height: clamp(120px, 55vh, 400px);
    }

    .nav-option {
        padding: 6px 8px;
        margin-left: 12px;
        margin-right: 12px;
        display: flex;
        align-items: center;
        gap: 4px;

        &:focus-visible {
            outline: 2px solid var(--text-body-default-base);
            outline-offset: -2px;
        }

        &:hover:not(.is-open, .selected) {
            background-color: var(--surface-default-hover);

            .filled-icon {
                opacity: 1;
            }

            .empty-icon {
                opacity: 0;
            }
        }

        &.selected {
            font-weight: 600;
            background-color: var(--surface-secondary-active);

            .filled-icon {
                opacity: 1;
            }

            .empty-icon {
                opacity: 0;
            }
        }

        &.is-open {
            background-color: var(--surface-default-hover);

            .filled-icon {
                opacity: 1;
            }

            .empty-icon {
                opacity: 0;
            }
        }
    }

    .nav-option__text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .nav-option__tag {
        margin-left: auto;
    }
}

.pendo-top-nav__left-option {
    color: var(--button-text-base);
    font-weight: 600;

    &:hover,
    &:focus-visible {
        color: var(--button-text-hover);
    }

    &:focus-visible {
        outline: 2px solid var(--text-body-default-base);
        border-radius: 5px;
        outline-offset: 1px;
    }

    .pendo-multiselect__trigger--data-measure::after {
        border-bottom: none;
    }

    &:hover .pendo-multiselect__trigger:not(.is-open, .is-disabled) {
        text-decoration: underline;
        text-decoration-thickness: var(--link-underline-thickness, 0.75px);
        text-underline-offset: var(--link-underline-offset, 3px);
        text-decoration-color: var(--button-text-hover);
    }

    .resource-center-guide-details-header-breadcrumb,
    .announcement-module-guide-details-header-breadcrumb {
        &:focus-visible {
            outline: 2px solid var(--text-body-default-base);
            border-radius: 5px;
            outline-offset: 2px;
        }

        &:focus {
            text-decoration: none;
        }

        &:hover {
            text-decoration: underline;
            text-decoration-thickness: var(--link-underline-thickness, 0.75px);
            text-underline-offset: var(--link-underline-offset, 3px);
            text-decoration-color: var(--button-text-hover);
        }
    }

    .pendo-multiselect__trigger--data-measure,
    .pendo-multiselect__trigger {
        background: var(--surface-default-background);
    }

    .pendo-multiselect__input {
        background: transparent;

        &-wrapper input {
            max-width: 110px;
        }
    }
}

a.pendo-top-nav__left-option {
    &:focus {
        text-decoration: none;
    }

    &:hover {
        text-decoration: underline;
        text-decoration-thickness: var(--link-underline-thickness, 0.75px);
        text-underline-offset: var(--link-underline-offset, 3px);
        text-decoration-color: var(--button-text-hover);
    }
}
</style>
